/**
 * CompareSubmenuRowContainer - Container for compare submenu rows
 *
 * Provides stable callback reference for item selection.
 * @remarks This is a Container component - handles callbacks and passes to View
 */

import React, { useCallback } from "react";

import CompareSubmenuRowView from "./CompareSubmenuRowView";

import type { CompareItem } from "@/features/player-detail/types";

/**
 * Props for CompareSubmenuRowContainer component
 */
interface CompareSubmenuRowContainerProps {
  /** The compare item to display */
  readonly item: CompareItem;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Handler for selecting the item */
  readonly onSelect: (item: CompareItem) => void;
}

/**
 * CompareSubmenuRowContainer wraps the view with stable callbacks
 * @param props - Container props
 * @param props.item - The compare item to display
 * @param props.isDark - Whether dark mode is active
 * @param props.onSelect - Handler for selecting the item
 * @returns CompareSubmenuRowView with stable callback
 */
const CompareSubmenuRowContainer = ({
  item,
  isDark,
  onSelect,
}: CompareSubmenuRowContainerProps) => {
  const handlePress = useCallback(() => {
    onSelect(item);
  }, [item, onSelect]);

  return (
    <CompareSubmenuRowView item={item} isDark={isDark} onPress={handlePress} />
  );
};

CompareSubmenuRowContainer.displayName = "CompareSubmenuRowContainer";

export default React.memo(CompareSubmenuRowContainer);
